import type { PublicSigninAnnouncement } from 'types';
import type { SigninAnnouncementFormValues } from '@/state/useSigninPanel';
import { useState } from 'react';
import { Globe, Loader2, Plus } from 'lucide-react';
import { SIGNIN_ANNOUNCEMENT_LIMITS as LIMITS } from 'definitions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ChatBubble } from '../../Authentication/chat/ChatBubble';
import { buildNewsChapters } from '../../Authentication/chat/news-script';
import { Panel } from '../../Dashboard/ConsolePanel';
import { BLANK_TEXT_REPLY, resolvePublishedAt, toDateInput } from './announcement-form';
import { ReplyEditor } from './ReplyEditor';

const FIELD_INPUT = 'border-line bg-field text-foreground h-10 rounded-lg text-body';
const FIELD_TEXTAREA = 'border-line bg-field text-foreground rounded-lg text-body';

type AnnouncementEditorProps = {
  initial: SigninAnnouncementFormValues;
  isNew: boolean;
  isSaving: boolean;
  onSave: (values: SigninAnnouncementFormValues) => void;
  onCancel: () => void;
};

type PreviewProps = { values: SigninAnnouncementFormValues };

/**
 * The draft as the login chat will play it, through the same bubble components. Not
 * the full chat: the pacing and the suggestions are the chat's job, and what an
 * author is checking here is the words.
 *
 * Gated on a headline for the same reason the API is: without one there is nothing
 * to show, and the real screen would fall back to the evergreen copy.
 */
const Preview = ({ values }: PreviewProps) => {
  if (!values.headline.trim()) {
    return <p className="supporting m-0">Add a headline to see how this reads on the signin screen.</p>;
  }

  const draft: PublicSigninAnnouncement = {
    id: 0,
    intro: values.intro.trim(),
    headline: values.headline.trim(),
    body: values.body.trim(),
    replies: values.replies.filter((reply) => reply.question.trim()),
    publishedAt: values.publishedAt,
  };
  const [chapter] = buildNewsChapters([draft]);

  return (
    <div className="flex flex-col gap-1 rounded-[16px] border border-[var(--accentLine)] bg-[color-mix(in_oklch,hsl(var(--accent))_7%,hsl(var(--panel)))] p-5">
      {chapter?.messages.map((message, idx) => (
        <div key={idx} className={message.kind === 'date' ? '' : 'mt-1'}>
          <ChatBubble message={message} />
        </div>
      ))}

      {chapter && chapter.questions.length > 0 && (
        <div className="mt-4 flex flex-col items-end gap-2">
          {chapter.questions.map((item) => (
            <span
              key={item.question}
              className="text-accent h-[34px] rounded-full border border-[var(--accentLine)] px-[15px] text-body leading-[34px] font-medium"
            >
              {item.question}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export const AnnouncementEditor = ({ initial, isNew, isSaving, onSave, onCancel }: AnnouncementEditorProps) => {
  const [form, setForm] = useState<SigninAnnouncementFormValues>(initial);
  const [dateInput, setDateInput] = useState(toDateInput(initial.publishedAt));

  const update = (values: Partial<SigninAnnouncementFormValues>) => setForm((current) => ({ ...current, ...values }));

  const publishedAt = resolvePublishedAt({ input: dateInput, current: initial.publishedAt });
  const values: SigninAnnouncementFormValues = { ...form, publishedAt };

  const changeReply = (idx: number, reply: SigninAnnouncementFormValues['replies'][number]) =>
    update({ replies: form.replies.map((item, at) => (at === idx ? reply : item)) });

  const removeReply = (idx: number) => update({ replies: form.replies.filter((_, at) => at !== idx) });

  return (
    <div className="flex flex-col gap-[14px]">
      <Panel
        title={form.active ? 'Published' : 'Draft'}
        description={
          form.active
            ? 'Once saved, this plays on the signin screen while it is among the newest published announcements.'
            : 'Not shown anywhere. Turn this on and save to publish it.'
        }
        actions={
          <Switch
            aria-label="Publish this announcement"
            checked={form.active}
            disabled={isSaving}
            onCheckedChange={(active) => update({ active })}
          />
        }
      />

      <Panel title="Preview" bodyClassName="flex flex-col gap-2 p-3.5">
        <Preview values={values} />
      </Panel>

      <Panel
        title={isNew ? 'New announcement' : 'Edit announcement'}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onCancel} disabled={isSaving} className="h-9 rounded-lg">
              Cancel
            </Button>
            <Button onClick={() => onSave(values)} disabled={isSaving} className="h-9 rounded-lg">
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </div>
        }
        bodyClassName="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-1.5 md:max-w-[220px]">
          <label htmlFor="signin-date" className="micro-label block">
            Date
          </label>
          <Input
            id="signin-date"
            type="date"
            value={dateInput}
            onChange={(ev) => setDateInput(ev.target.value)}
            className={FIELD_INPUT}
          />
          <p className="supporting m-0">
            Shown on the announcement, and what orders it against the others. Set it to when the change shipped.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="signin-intro" className="micro-label block">
            Intro (optional)
          </label>
          <Input
            id="signin-intro"
            value={form.intro}
            maxLength={LIMITS.intro}
            placeholder="Hey, welcome back."
            onChange={(ev) => update({ intro: ev.target.value })}
            className={FIELD_INPUT}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="signin-headline" className="micro-label block">
            Headline
          </label>
          <Input
            id="signin-headline"
            value={form.headline}
            maxLength={LIMITS.headline}
            placeholder="One less password to remember."
            onChange={(ev) => update({ headline: ev.target.value })}
            className={FIELD_INPUT}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="signin-body" className="micro-label block">
            Body (optional)
          </label>
          <Textarea
            id="signin-body"
            value={form.body}
            maxLength={LIMITS.body}
            rows={3}
            placeholder="You can now sign in with a passkey. Use your face, fingerprint or security key, and you are in."
            onChange={(ev) => update({ body: ev.target.value })}
            className={FIELD_TEXTAREA}
          />
          <p className="supporting m-0 flex items-start gap-1.5">
            <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Once published, anyone who opens the signin page can read this, signed in or not.
          </p>
        </div>
      </Panel>

      <Panel
        title="Suggested questions"
        description={`Up to ${LIMITS.replies}. The visitor taps one and Pertento answers with text or a link card.`}
        actions={
          <Button
            variant="outline"
            onClick={() => update({ replies: [...form.replies, BLANK_TEXT_REPLY] })}
            disabled={form.replies.length >= LIMITS.replies}
            className="h-9 rounded-lg"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add question
          </Button>
        }
        bodyClassName="flex flex-col gap-3"
      >
        {form.replies.length === 0 && <p className="supporting m-0">No questions. The announcement just plays.</p>}
        {form.replies.map((reply, idx) => (
          <ReplyEditor
            key={idx}
            reply={reply}
            position={idx + 1}
            onChange={(next) => changeReply(idx, next)}
            onRemove={() => removeReply(idx)}
          />
        ))}
      </Panel>
    </div>
  );
};
